import React, { useState, useEffect } from 'react';

export default function Webform({ defaultSubject = "" }) {
  const [formData, setFormData] = useState({
    navn: '',
    telefon: '',
    email: '',
    emne: defaultSubject || 'Generel forespørgsel',
    beskrivelse: '',
    website_url: '' // Honeypot
  });

  useEffect(() => {
    if (defaultSubject) {
      setFormData(prev => ({ ...prev, emne: defaultSubject }));
    }
  }, [defaultSubject]);

  const [status, setStatus] = useState({ state: 'idle', message: '' });

  function handleChange(e) {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  }

  async function handleSubmit(e) {
    e.preventDefault();

    // Simpel klientside validering
    if (!formData.navn.trim() || !formData.email.trim() || !formData.beskrivelse.trim()) {
      setStatus({ state: 'error', message: 'Udfyld venligst alle obligatoriske felter (Navn, Email og Beskrivelse).' });
      return;
    }

    setStatus({ state: 'submitting', message: 'Sender din henvendelse...' });

    try {
      const payload = {
        navn: formData.navn,
        telefon: formData.telefon,
        email: formData.email,
        emne: formData.emne,
        beskrivelse: formData.beskrivelse,
        website_url: formData.website_url // Honeypot felt
      };

      const res = await fetch('https://api.dev.auxo.dk/webform/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      if (res.ok) {
        setStatus({
          state: 'success',
          message: 'Tusind tak for din henvendelse! Vi vender tilbage hurtigst muligt.'
        });
        setFormData({
          navn: '',
          telefon: '',
          email: '',
          emne: 'Generel forespørgsel',
          beskrivelse: '',
          website_url: ''
        });
      } else {
        const data = await res.json().catch(() => ({}));
        setStatus({
          state: 'error',
          message: data.message || 'Der opstod en fejl ved afsendelsen. Prøv venligst igen eller skriv direkte til info@orbike.dk.'
        });
      }
    } catch {
      setStatus({
        state: 'error',
        message: 'Netværksfejl. Tjek din internetforbindelse eller send os en email på info@orbike.dk.'
      });
    }
  }

  return (
    <div className="bg-[#12141f]/95 border border-pink-500/30 rounded-2xl p-6 sm:p-8 backdrop-blur-md shadow-[0_0_30px_rgba(255,42,141,0.15)] relative overflow-hidden">
      <div className="absolute top-0 inset-x-0 h-1 bg-gradient-to-r from-pink-500 via-purple-500 to-pink-500"></div>

      <div className="mb-6">
        <span className="text-xs uppercase tracking-widest font-bold text-pink-400 font-mono">
          Skriv til arrangørerne
        </span>
        <h3 className="text-2xl sm:text-3xl font-extrabold text-white font-display uppercase tracking-wide mt-1">
          Har du spørgsmål eller ønsker et partnerskab?
        </h3>
        <p className="text-slate-400 text-sm mt-1">
          Udfyld formularen nedenfor, så kontakter vi dig hurtigst muligt.
        </p>
      </div>

      {status.state === 'success' ? (
        <div className="bg-emerald-950/40 border border-emerald-500/40 rounded-xl p-6 text-center animate-in fade-in duration-300">
          <div className="w-14 h-14 bg-emerald-500/20 text-emerald-400 rounded-full flex items-center justify-center mx-auto mb-3 text-2xl">
            <i className="fa-solid fa-circle-check" aria-hidden="true"></i>
          </div>
          <h4 className="text-xl font-bold text-white font-display uppercase">Beskeden er modtaget!</h4>
          <p className="text-emerald-200 text-sm mt-2">{status.message}</p>
          <button
            type="button"
            onClick={() => setStatus({ state: 'idle', message: '' })}
            className="mt-5 px-6 py-2 rounded-full bg-emerald-600 hover:bg-emerald-500 text-white font-semibold text-xs uppercase tracking-wider transition-colors"
          >
            Send en ny besked
          </button>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-4 text-left">
          {/* Honeypot field (Strictly invisible) */}
          <input
            type="text"
            name="website_url"
            value={formData.website_url}
            onChange={handleChange}
            className="sr-only absolute -top-[9999px] -left-[9999px]"
            tabIndex={-1}
            autoComplete="off"
            aria-hidden="true"
          />

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label htmlFor="navn" className="block text-xs uppercase font-bold text-slate-300 mb-1">
                Navn <span className="text-pink-500">*</span>
              </label>
              <input
                type="text"
                id="navn"
                name="navn"
                required
                value={formData.navn}
                onChange={handleChange}
                placeholder="Dit fulde navn"
                className="w-full px-4 py-3 rounded-xl bg-slate-900/80 border border-slate-700 text-white placeholder-slate-500 focus:outline-none focus:border-pink-500 focus:ring-1 focus:ring-pink-500 transition-colors text-sm"
              />
            </div>

            <div>
              <label htmlFor="telefon" className="block text-xs uppercase font-bold text-slate-300 mb-1">
                Telefonnummer
              </label>
              <input
                type="tel"
                id="telefon"
                name="telefon"
                value={formData.telefon}
                onChange={handleChange}
                placeholder="+45 12 34 56 78"
                className="w-full px-4 py-3 rounded-xl bg-slate-900/80 border border-slate-700 text-white placeholder-slate-500 focus:outline-none focus:border-pink-500 focus:ring-1 focus:ring-pink-500 transition-colors text-sm"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label htmlFor="email" className="block text-xs uppercase font-bold text-slate-300 mb-1">
                Email adresse <span className="text-pink-500">*</span>
              </label>
              <input
                type="email"
                id="email"
                name="email"
                required
                value={formData.email}
                onChange={handleChange}
                placeholder="din@email.dk"
                className="w-full px-4 py-3 rounded-xl bg-slate-900/80 border border-slate-700 text-white placeholder-slate-500 focus:outline-none focus:border-pink-500 focus:ring-1 focus:ring-pink-500 transition-colors text-sm"
              />
            </div>

            <div>
              <label htmlFor="emne" className="block text-xs uppercase font-bold text-slate-300 mb-1">
                Emne
              </label>
              <select
                id="emne"
                name="emne"
                value={formData.emne}
                onChange={handleChange}
                className="w-full px-4 py-3 rounded-xl bg-slate-900/80 border border-slate-700 text-white focus:outline-none focus:border-pink-500 focus:ring-1 focus:ring-pink-500 transition-colors text-sm"
              >
                <option value="Generel forespørgsel">Generel forespørgsel</option>
                <option value="ØrBike 365">ØrBike 365</option>
                <option value="Billetter">Billetter</option>
                <option value="Forplejning">Forplejning</option>
                <option value="Partnerskab / Sponsor">Partnerskab / Sponsor</option>
                <option value="Priority Cykler">Priority Cykler</option>
                <option value="Bogholderi">Bogholderi</option>
              </select>
            </div>
          </div>

          <div>
            <label htmlFor="beskrivelse" className="block text-xs uppercase font-bold text-slate-300 mb-1">
              Beskrivelse <span className="text-pink-500">*</span>
            </label>
            <textarea
              id="beskrivelse"
              name="beskrivelse"
              rows={4}
              required
              value={formData.beskrivelse}
              onChange={handleChange}
              placeholder="Hvordan kan vi hjælpe dig?"
              className="w-full px-4 py-3 rounded-xl bg-slate-900/80 border border-slate-700 text-white placeholder-slate-500 focus:outline-none focus:border-pink-500 focus:ring-1 focus:ring-pink-500 transition-colors text-sm resize-y"
            ></textarea>
          </div>

          {status.state === 'error' && (
            <div className="p-3 bg-red-950/50 border border-red-500/40 rounded-lg text-red-200 text-xs flex items-center gap-2">
              <i className="fa-solid fa-triangle-exclamation text-red-400" aria-hidden="true"></i>
              <span>{status.message}</span>
            </div>
          )}

          <button
            type="submit"
            disabled={status.state === 'submitting'}
            className="w-full py-3.5 px-6 rounded-xl bg-gradient-to-r from-pink-500 to-purple-600 hover:from-pink-600 hover:to-purple-700 text-white font-bold text-sm uppercase tracking-wider shadow-[0_0_20px_rgba(255,42,141,0.4)] transition-all hover:scale-[1.01] active:scale-95 disabled:opacity-50 disabled:pointer-events-none flex items-center justify-center gap-2"
          >
            {status.state === 'submitting' ? (
              <>
                <i className="fa-solid fa-spinner fa-spin" aria-hidden="true"></i>
                <span>Sender...</span>
              </>
            ) : (
              <>
                <span>SEND NU</span>
                <i className="fa-solid fa-arrow-right" aria-hidden="true"></i>
              </>
            )}
          </button>
        </form>
      )}
    </div>
  );
}
